import React from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'
import useSWRImmutable from 'swr/immutable'

import ClientSelector from '../clients/selector'
import ClientModal from '../clients/modal'
import InvoiceOverlayModal from '../modals'
import TaxUpdateScopeModal from './taxUpdateScopeModal'
import ZeroTotalSendModal from './zeroTotalSendModal'
import InvoiceItems from './invoiceItems'
import BusinessModal, { BusinessDetailsDraft, BusinessModalInitialFocus } from '../business/modal'
import { useAlerts } from '../../context/alerts'
import Input, { CalculatedInput } from '../ui/input'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import SentToClientToggle from './sentToClientToggle'
import { useAuth } from '../../context/auth'
import * as utils from '../../utils'
import {
  Client,
  InvoiceActivity,
  InvoiceItem,
  InvoiceStatus,
  TBusiness,
  TClientForm,
  TFormError,
  TInvoiceForm,
} from '../../types'
import StatusSelector from './statusSelector'
import TextArea from '../ui/input/textArea'
import InvoiceFormActions from './invoiceFormActions'
import GuestInvoiceActions from './guestInvoiceActions'
import { resolveInvoiceFormActionSurfaces } from './invoiceFormActionSurfaces'
import {
  getClients,
  getCurrencies,
  getEmailTemplate,
  getInvoiceById,
  getInvoiceHistory,
  markInvoiceSentManually,
  saveOrUpdateInvoice,
  unmarkInvoiceSentManually,
  updateBusinessDetails,
  updateUnsentInvoicesTax,
} from '../../api'
import { formatDateShort } from '../../lib/dates'
import { clearNewInvoiceDraftStorage, NEW_INVOICE_DRAFT_STORAGE_KEY, saveNewInvoiceDraft } from '../../lib/invoiceDraftStorage'
import { normalizeDraftItems } from '../../lib/invoiceDraftItems'
import { revalidateClientCaches } from '../../lib/revalidateClientCaches'
import { revalidateInvoiceSummaryCaches } from '../../lib/revalidateInvoiceSummaryCaches'
import type { SavedClientSnapshot } from '../clients/form'
import './styles.scss'
import config from '../../config'
import { isInvoiceSentToClient } from '../../lib/invoiceDelivery'
import BusinessLogoEditor from '../business/logoEditor'
import GuestDraftClientFields from './guestDraftClientFields'
import GuestInlineBusinessFields from './guestInlineBusinessFields'
import GuestTaxLink from './guestTaxLink'
import GuestTaxModal, { buildGuestTaxDraft, GuestTaxDraft } from './guestTaxModal'
import {
  DraftClient,
  EMPTY_DRAFT_CLIENT,
  GUEST_INVOICE_DRAFT_VERSION,
  GuestPendingIntent,
  loadGuestInvoiceDraft,
  parseDraftClient,
  saveGuestInvoiceDraft,
} from '../../lib/guestInvoiceDraftStorage'
import { getStaticCurrencyByCode } from '../../lib/currencies'
import { ReactComponent as PlusIcon} from '../../static/images/plus.svg'
import { ReactComponent as EditIcon} from '../../static/images/edit.svg'
import FieldLabel from '../ui/input/FieldLabel'
import { usePageHeaderOptional, type PageHeaderConfig } from '../../context/pageHeader'
import useScreenSize from '../../context/screenSize'
import { useInvoiceFormBottomBarOffset } from '../../hooks/useInvoiceFormBottomBarOffset'
import { useMobileSidebarOpen } from '../../hooks/useMobileSidebarOpen'
import { useVirtualKeyboardOpen } from '../../hooks/useVirtualKeyboardOpen'
import { EMPTY_ITEM } from './constants'

export const INVOICE_FORM_ID = 'invoice-form'

const noopSetPageHeader = (_patch: Partial<PageHeaderConfig>) => {}

const MANUAL_SENT_HISTORY_MESSAGE = 'Marked as sent to client'

/** Derive summary totals from line items so loaded invoices display correctly before any edit. */
export const withComputedInvoiceTotals = (values: TInvoiceForm): TInvoiceForm => {
  const enableTax = !!values.businessDetails?.enableTax
  const { total, totalTax } = utils.computeInvoiceTotals(values.items ?? [], enableTax)
  return { ...values, total, totalTax }
}

const parseHandoffDraftClient = (value: unknown): DraftClient | undefined => {
  const parsed = parseDraftClient(value)
  if (!parsed) return undefined
  if (!parsed.tradingName && !parsed.email && !parsed.billingAddress) return undefined
  return parsed
}

const loadNewInvoiceDraft = (
  fallbackValues: TInvoiceForm,
  disableDraftRestore: boolean,
): { values: TInvoiceForm; paymentInstructionsDraft?: string; draftClient?: DraftClient } => {
  if (disableDraftRestore || fallbackValues._id || typeof window === 'undefined') {
    return { values: fallbackValues }
  }
  try {
    const raw = window.localStorage.getItem(NEW_INVOICE_DRAFT_STORAGE_KEY)
    if (!raw) return { values: fallbackValues }
    const parsed = JSON.parse(raw)
    let values =
      parsed?.values && typeof parsed.values === 'object'
        ? { ...fallbackValues, ...parsed.values }
        : fallbackValues
    values = { ...values, items: normalizeDraftItems(values.items) }
    const paymentInstructionsDraft =
      typeof parsed?.paymentInstructionsDraft === 'string'
        ? parsed.paymentInstructionsDraft
        : undefined
    return { values, paymentInstructionsDraft, draftClient: parseHandoffDraftClient(parsed?.draftClient) }
  } catch {
    return { values: fallbackValues }
  }
}

const persistNewInvoiceDraft = (
  values: TInvoiceForm,
  paymentInstructionsDraft: string,
  draftClient?: DraftClient,
) => {
  if (values._id || typeof window === 'undefined') return
  saveNewInvoiceDraft({
    values: { ...values, items: normalizeDraftItems(values.items) },
    paymentInstructionsDraft,
    draftClient: values.clientId ? undefined : draftClient,
  })
}

const definedPaymentInstructions = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  return value
}

export const resolvePaymentInstructions = (
  isNewInvoice: boolean,
  invoicePI: string | undefined,
  userPI: string | undefined,
  draftPI?: string,
  options?: { preferInvoice?: boolean },
) => {
  const invoice = definedPaymentInstructions(invoicePI)
  const user = definedPaymentInstructions(userPI)
  const draft = definedPaymentInstructions(draftPI)
  if (isNewInvoice && options?.preferInvoice) {
    return invoice ?? user ?? ''
  }
  if (isNewInvoice) {
    return draft ?? user ?? invoice ?? ''
  }
  return invoice ?? draft ?? ''
}

export {
  resolveInvoiceFormActionSurfaces,
  type InvoiceFormActionId,
  type InvoiceFormActionSurfaces,
  type InvoiceFormMenuActionId,
  type ResolveInvoiceFormActionSurfacesInput,
} from './invoiceFormActionSurfaces'

/** Returns yyyy-mm-dd for <input type='date'>. Display format is controlled by the browser. */
const toDateInputValue = (date: Date | string): string => {
  const d = new Date(date)
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

/** Top accent on the invoice sheet; matches dashboard summary card colors (see summaryCards/styles.scss). */
const invoiceSheetStatusClass = (status: InvoiceStatus | undefined): string => {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'invoice-sheet--status-paid'
    case InvoiceStatus.OVERDUE:
      return 'invoice-sheet--status-overdue'
    case InvoiceStatus.DRAFT:
      return 'invoice-sheet--status-draft'
    case InvoiceStatus.OPEN:
    default:
      return 'invoice-sheet--status-open'
  }
}

export type ItemFieldKey = 'description' | 'quantity' | 'rate'
export type ItemFieldErrors = Partial<Record<ItemFieldKey, true>>
export type ItemValidationErrors = Record<number, ItemFieldErrors>

const ITEM_FIELD_ORDER: ItemFieldKey[] = ['description', 'quantity', 'rate']

export const ITEM_FIELD_MESSAGES: Record<ItemFieldKey, string> = {
  description: 'Please add at least one line item',
  quantity: 'Please add item quantity',
  rate: 'Please add item rate',
}

export const hasItemDescription = (item: InvoiceItem): boolean =>
  !!item.description?.trim()

export const hasItemQuantity = (item: InvoiceItem): boolean => {
  if (item.quantity === '' || item.quantity == null) return false
  const qty = parseFloat(item.quantity.toString())
  return !Number.isNaN(qty) && qty >= 0
}

export const hasItemRate = (item: InvoiceItem): boolean => {
  if (item.rate == null || item.rate === '') return false
  const rate = parseFloat(item.rate.toString())
  return !Number.isNaN(rate) && rate >= 0
}

export const hasItemRateGreaterThanZero = (item: InvoiceItem): boolean => {
  if (item.rate == null || item.rate === '') return false
  const rate = parseFloat(item.rate.toString())
  return !Number.isNaN(rate) && rate > 0
}

export const invoiceHasAnyItemWithRateGreaterThanZero = (items: InvoiceItem[]): boolean =>
  items.some(hasItemRateGreaterThanZero)

export const isItemQuantityNegative = (item: InvoiceItem): boolean => {
  if (item.quantity === '' || item.quantity == null) return false
  const qty = parseFloat(item.quantity.toString())
  return !Number.isNaN(qty) && qty < 0
}

export const isItemComplete = (item: InvoiceItem): boolean =>
  hasItemDescription(item) && hasItemQuantity(item) && hasItemRate(item)

export const isItemRowStarted = (item: InvoiceItem): boolean =>
  hasItemDescription(item)
  || hasItemRate(item)
  || (item.quantity !== '' && item.quantity != null && item.quantity !== 1)

export const getInvalidItemFields = (items: InvoiceItem[]): ItemValidationErrors => {
  const result: ItemValidationErrors = {}
  const hasCompleteItem = items.some(isItemComplete)

  items.forEach((item, index) => {
    const shouldValidate = !hasCompleteItem
      ? items.length === 0 || isItemRowStarted(item) || index === 0
      : isItemRowStarted(item)

    if (!shouldValidate || isItemComplete(item)) {
      return
    }

    const fieldErrors: ItemFieldErrors = {}

    if (!hasItemDescription(item)) {
      fieldErrors.description = true
    }
    if (isItemQuantityNegative(item)) {
      fieldErrors.quantity = true
    } else if (hasItemDescription(item) && !hasItemQuantity(item)) {
      fieldErrors.quantity = true
    }
    if ((hasItemDescription(item) || hasItemQuantity(item)) && !hasItemRate(item)) {
      fieldErrors.rate = true
    }

    if (Object.keys(fieldErrors).length > 0) {
      result[index] = fieldErrors
    }
  })

  return result
}

export const getFirstItemValidationMessage = (
  items: InvoiceItem[],
  fieldErrors: ItemValidationErrors,
): string => {
  for (let index = 0; index < items.length; index++) {
    const rowErrors = fieldErrors[index]
    if (!rowErrors) continue
    for (const field of ITEM_FIELD_ORDER) {
      if (rowErrors[field]) {
        if (field === 'quantity' && isItemQuantityNegative(items[index])) {
          return 'Item quantity cannot be negative'
        }
        return ITEM_FIELD_MESSAGES[field]
      }
    }
  }
  return ITEM_FIELD_MESSAGES.description
}

export const validateForm = (formValues: TInvoiceForm): Array<TFormError> => {
  const errors = []

  if(!formValues.code || formValues.code.trim() === '') {
    errors.push({ type: 'error', message: 'Please set the invoice reference' })
  } else if (formValues.items.length === 0) {
    errors.push({ type: 'error', message: ITEM_FIELD_MESSAGES.description })
  } else if (!formValues.items.some(isItemComplete)) {
    const fieldErrors = getInvalidItemFields(formValues.items)
    if (Object.keys(fieldErrors).length > 0) {
      errors.push({
        type: 'error',
        message: getFirstItemValidationMessage(formValues.items, fieldErrors),
      })
    }
  } else if(new Date(formValues.issueDate) > new Date(formValues.dueDate)) {
    errors.push({ type: 'error', message: 'Due Date can not be before Issue Date' })
  } else if (formValues?.businessDetails?.enableTax && !formValues?.businessDetails?.tax?.name) {
    errors.push({ type: 'error', message: 'Please complete your tax information' })
  }
  return errors
}

export const hasSelectedInvoiceClient = (
  clientId: string | undefined,
  clients: Client[] | undefined,
  locked = false,
): boolean => {
  const id = clientId != null ? String(clientId).trim() : ''
  if (!id) return false
  if (locked) return true
  if (!clients || clients.length === 0) return false
  return clients.some((client) => String(client._id) === id)
}

export const isCompleteDraftClient = (draftClient: DraftClient | undefined): boolean => {
  if (!draftClient) return false
  return !!draftClient.tradingName?.trim()
}

export const resolveHandoffCreateClientDefaults = (
  handoffDraftClient: DraftClient | undefined,
  clientCount: number,
): TClientForm | undefined => {
  if (!handoffDraftClient || clientCount > 0) return undefined
  return {
    'client.tradingName': handoffDraftClient.tradingName,
    'client.email': handoffDraftClient.email,
    'client.billingAddress': handoffDraftClient.billingAddress,
    'client.name': handoffDraftClient.name ?? '',
    'client.lastName': handoffDraftClient.lastName ?? '',
  }
}

export const isCompleteGuestBusinessDetails = (
  businessDetails: TInvoiceForm['businessDetails'] | undefined,
): boolean => {
  if (!businessDetails) return false
  const name = businessDetails.name?.trim()
  const currencyCode = businessDetails.currency?.code?.trim()
  if (!name) return false
  if (!currencyCode || !getStaticCurrencyByCode(currencyCode)) return false
  return true
}

export const isGuestTaxConfigured = (businessDetails: TBusiness | undefined): boolean => {
  if (!businessDetails) return false
  return Boolean(
    businessDetails.taxRegistrationNumber?.trim()
    || businessDetails.enableTax,
  )
}

export type { DraftClient }

export type InvoiceValidationScrollTarget =
  | { kind: 'client' }
  | { kind: 'business' }
  | { kind: 'items' }
  | { kind: 'field'; name: string }
  | { kind: 'item'; index: number; field: ItemFieldKey }

export const getValidationScrollTarget = (formValues: TInvoiceForm): InvoiceValidationScrollTarget | null => {
  if (!formValues.code || formValues.code.trim() === '') {
    return { kind: 'field', name: 'code' }
  }
  if (formValues.items.length === 0) {
    return { kind: 'items' }
  }
  if (!formValues.items.some(isItemComplete)) {
    const fieldErrors = getInvalidItemFields(formValues.items)
    for (let index = 0; index < formValues.items.length; index++) {
      const rowErrors = fieldErrors[index]
      if (!rowErrors) continue
      for (const field of ITEM_FIELD_ORDER) {
        if (rowErrors[field]) {
          return { kind: 'item', index, field }
        }
      }
    }
  }
  if (new Date(formValues.issueDate) > new Date(formValues.dueDate)) {
    return { kind: 'field', name: 'dueDate' }
  }
  if (formValues?.businessDetails?.enableTax && !formValues?.businessDetails?.tax?.name) {
    return { kind: 'business' }
  }
  return null
}

const ITEM_FIELD_INPUT_NAMES: Record<ItemFieldKey, (index: number) => string> = {
  description: (index) => `invoiceItemDescription${index}`,
  quantity: (index) => `invoiceItemQuantity${index}`,
  rate: (index) => `invoiceItemRate${index}`,
}

const findVisibleFormControl = (root: ParentNode, name: string, id?: string): HTMLElement | null => {
  if (id) {
    const byId = root.querySelector(`#${CSS.escape(id)}`) as HTMLElement | null
    if (byId?.offsetParent !== null) return byId
  }
  const controls = root.querySelectorAll(`[name="${name}"]`)
  for (let i = 0; i < controls.length; i++) {
    const control = controls[i]
    if (control instanceof HTMLElement && control.offsetParent !== null) {
      return control
    }
  }
  return (controls[0] as HTMLElement | undefined) ?? null
}

const reportNativeFieldValidity = (el: HTMLElement) => {
  if (
    el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el instanceof HTMLSelectElement
  ) {
    el.reportValidity()
  }
}

export const resolveValidationScrollElement = (
  target: InvoiceValidationScrollTarget,
  root: ParentNode = document,
): HTMLElement | null => {
  switch (target.kind) {
    case 'client':
      return root.querySelector('[data-invoice-field="client"]') as HTMLElement | null
    case 'business':
      return root.querySelector('[data-invoice-field="business"]') as HTMLElement | null
    case 'items':
      return root.querySelector('[data-invoice-field="items"]') as HTMLElement | null
    case 'field':
      return (
        (root.querySelector(`#${target.name}`) as HTMLElement | null)
        ?? (root.querySelector(`[name="${target.name}"]`) as HTMLElement | null)
      )
    case 'item': {
      const row = root.querySelector(`#invoice-item-${target.index}`)
      if (!row) return null
      const inputName = ITEM_FIELD_INPUT_NAMES[target.field](target.index)
      return (
        findVisibleFormControl(row, inputName, inputName)
        ?? (row as HTMLElement)
      )
    }
    default:
      return null
  }
}

const focusValidationElement = (el: HTMLElement) => {
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    el.focus({ preventScroll: true })
    return
  }
  const focusable = el.querySelector<HTMLElement>(
    'input:not([type="hidden"]), textarea, select, [role="combobox"]',
  )
  focusable?.focus({ preventScroll: true })
}

export const scrollToValidationTarget = (
  target: InvoiceValidationScrollTarget | null,
  root?: ParentNode | null,
) => {
  if (!target) return
  const scope = root ?? document
  const el = resolveValidationScrollElement(target, scope)
  if (!el) return
  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    focusValidationElement(el)
    if (target.kind === 'item' || target.kind === 'field') {
      reportNativeFieldValidity(el)
    }
  })
}

export const scrollToFirstInvalidField = (formEl: HTMLFormElement) => {
  const invalidFields = formEl.querySelectorAll(':invalid')
  let firstInvalid: HTMLElement | null = null
  for (let i = 0; i < invalidFields.length; i++) {
    const field = invalidFields[i]
    if (field instanceof HTMLElement && field.offsetParent !== null) {
      firstInvalid = field
      break
    }
  }
  if (!firstInvalid) return
  reportNativeFieldValidity(firstInvalid)
  requestAnimationFrame(() => {
    firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    firstInvalid?.focus({ preventScroll: true })
  })
}

export type InvoiceSaveSuccessContext = {
  wasNewInvoice: boolean
  values: TInvoiceForm
  redirectTo?: 'edit' | 'send-email'
}

type Props = {
  id?: string,
  onSuccess?: (result: any, context: InvoiceSaveSuccessContext) => void | Promise<void>,
  triggerRemoveInvoice?: (e: any) => void,
  isDeleting?: boolean,
  defaultValues: TInvoiceForm,
  disableDraftRestore?: boolean,
  preferInvoicePaymentInstructions?: boolean,
  guestMode?: boolean,
  onGuestSignInRequest?: (intent: GuestPendingIntent) => void,
  guestSignInDismissNonce?: number,
  externalBusinessDetailsApply?: {
    nonce: number
    draft: BusinessDetailsDraft
  },
}

const InvoiceForm: React.FC<Props> = ({
  defaultValues,
  onSuccess,
  triggerRemoveInvoice,
  isDeleting = false,
  disableDraftRestore = false,
  preferInvoicePaymentInstructions = false,
  guestMode = false,
  onGuestSignInRequest,
  guestSignInDismissNonce = 0,
  externalBusinessDetailsApply,
}) => {
  const { user, mutate: mutateUser } = useAuth()
  const { isMobile } = useScreenSize()
  const setPageHeader = usePageHeaderOptional()?.setPageHeader ?? noopSetPageHeader
  const triggerRemoveInvoiceRef = React.useRef(triggerRemoveInvoice)
  triggerRemoveInvoiceRef.current = triggerRemoveInvoice
  // routing
  const navigate = useNavigate()

  // form management — hydrate draft synchronously so a late mount effect cannot overwrite clientId
  const initialGuestDraft = guestMode ? loadGuestInvoiceDraft(defaultValues) : null
  const initialNewInvoiceDraft = guestMode
    ? null
    : loadNewInvoiceDraft(defaultValues, disableDraftRestore)
  const [values, setValues] = React.useState(() =>
    withComputedInvoiceTotals(
      guestMode
        ? (initialGuestDraft?.values ?? defaultValues)
        : (initialNewInvoiceDraft?.values ?? defaultValues),
    ),
  )
  const [draftClient, setDraftClient] = React.useState<DraftClient>(
    () => initialGuestDraft?.draftClient ?? EMPTY_DRAFT_CLIENT,
  )
  const [handoffDraftClient, setHandoffDraftClient] = React.useState<DraftClient | undefined>(
    () => initialNewInvoiceDraft?.draftClient,
  )
  const [guestLogoSrc] = React.useState<string | undefined>(
    () => initialGuestDraft?.logoSrc,
  )
  const guestPendingIntentRef = React.useRef<GuestPendingIntent | undefined>(
    initialGuestDraft?.pendingIntent,
  )

  const [isPristine, setPristine] = React.useState(!values._id ? false : true)
  const [isSendingToEmail, setIsSendingToEmail] = React.useState(false)
  const [clientSelectorNonce, setClientSelectorNonce] = React.useState(0)
  // data
  const { data: currenciesFromApi } = useSWRImmutable(
    guestMode ? null : '/api/invoices/currencies',
    getCurrencies,
  )
  const currenciesData = currenciesFromApi
  const { data: clients } = useSWR(guestMode ? null : '/api/clients', getClients)
  const { mutate: mutateCache } = useSWRConfig()
  const { trigger: triggerSaveInvoice, isMutating: isSavingInvoice } = useSWRMutation('/api/invoices', saveOrUpdateInvoice)
  const { trigger: triggerUpdateBusinessDetails, isMutating: isUpdatingBusinessDetails } = useSWRMutation('/api/user/business', updateBusinessDetails)
  const { trigger: triggerUpdateUnsentInvoicesTax, isMutating: isUpdatingUnsentInvoicesTax } = useSWRMutation('/api/invoices/tax/unsent', updateUnsentInvoicesTax)
  const { trigger: triggerMarkSentManually } = useSWRMutation(
    '/api/invoices/mark-sent-manually',
    markInvoiceSentManually,
  )
  const { trigger: triggerUnmarkSentManually } = useSWRMutation(
    '/api/invoices/unmark-sent-manually',
    unmarkInvoiceSentManually,
  )
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false)
  const [isSavingDraft, setIsSavingDraft] = React.useState(false)
  const [isTogglingSentToClient, setIsTogglingSentToClient] = React.useState(false)
  const [isPreparingPdf, setIsPreparingPdf] = React.useState(false)
  const isLoading =
    (isSavingInvoice && !isUpdatingStatus && !isTogglingSentToClient && !isPreparingPdf)
    || isUpdatingBusinessDetails
    || isSendingToEmail
    || isDeleting
  // alerts
  const { addAlert } = useAlerts()
  const [paymentInstructionsDraft, setPaymentInstructionsDraft] = React.useState(() => {
    if (guestMode) {
      return initialGuestDraft?.paymentInstructionsDraft ?? ''
    }
    const draft = initialNewInvoiceDraft
    const isNew = !defaultValues._id
    return resolvePaymentInstructions(
      isNew,
      defaultValues?.businessDetails?.paymentInstructions,
      user?.businessDetails?.paymentInstructions,
      draft?.paymentInstructionsDraft,
      { preferInvoice: preferInvoicePaymentInstructions },
    )
  })
  const [showCreateClientModal, setShowCreateClientModal] = React.useState(false)
  const [showEditClientModal, setShowEditClientModal] = React.useState(false)
  const [showEditBusinessModal, setShowEditBusinessModal] = React.useState(false)
  const [businessModalInitialFocus, setBusinessModalInitialFocus] =
    React.useState<BusinessModalInitialFocus>('tradingName')

  const openBusinessModal = React.useCallback((focus: BusinessModalInitialFocus = 'tradingName') => {
    setBusinessModalInitialFocus(focus)
    setShowEditBusinessModal(true)
  }, [])

  const closeBusinessModal = React.useCallback(() => {
    setShowEditBusinessModal(false)
    setBusinessModalInitialFocus('tradingName')
  }, [])
  const [showDuplicateConfirmModal, setShowDuplicateConfirmModal] = React.useState(false)
  const [showGuestTaxModal, setShowGuestTaxModal] = React.useState(false)
  const [showZeroTotalSendModal, setShowZeroTotalSendModal] = React.useState(false)
  const [showTaxUpdateScopeModal, setShowTaxUpdateScopeModal] = React.useState(false)
  const [pendingTaxDraft, setPendingTaxDraft] = React.useState<BusinessDetailsDraft | null>(null)
  const [prereqClientError, setPrereqClientError] = React.useState(false)
  const [prereqBusinessError, setPrereqBusinessError] = React.useState(false)
  const [itemFieldErrors, setItemFieldErrors] = React.useState<ItemValidationErrors>({})
  const [itemsEmptyError, setItemsEmptyError] = React.useState(false)
  const [itemsFlashNonce, setItemsFlashNonce] = React.useState(0)
  const [clientFlashNonce, setClientFlashNonce] = React.useState(0)
  const [businessFlashNonce, setBusinessFlashNonce] = React.useState(0)
  const formRef = React.useRef<HTMLFormElement>(null)
  const isNewInvoice = !values._id

  React.useEffect(() => {
    if (hasSelectedInvoiceClient(values.clientId, clients, defaultValues.locked)) {
      setPrereqClientError(false)
    } else if (!defaultValues.locked) {
      setItemsEmptyError(false)
      setItemFieldErrors({})
    }
  }, [values.clientId, clients, defaultValues.locked])

  React.useEffect(() => {
    if (guestMode) {
      if (isCompleteGuestBusinessDetails(values.businessDetails)) {
        setPrereqBusinessError(false)
      }
      return
    }
    if (user?.businessDetails?.name && String(user.businessDetails.name).trim() !== '') {
      setPrereqBusinessError(false)
    }
  }, [guestMode, values.businessDetails, user?.businessDetails?.name])

  React.useEffect(() => {
    if (defaultValues.locked) return
    if (handoffDraftClient) return
    if (clients?.length === 1 && clients[0]._id && !values.clientId) {
      setValues((prev: any) => ({ ...prev, clientId: String(clients[0]._id) }))
    }
  }, [clients, values.clientId, defaultValues.locked, handoffDraftClient])

  React.useEffect(() => {
    if (guestMode) return
    if (!isNewInvoice || typeof window === 'undefined') return
    persistNewInvoiceDraft(values, paymentInstructionsDraft, handoffDraftClient)
  }, [guestMode, isNewInvoice, values, paymentInstructionsDraft, handoffDraftClient])

  React.useEffect(() => {
    if (!guestMode || typeof window === 'undefined') return
    saveGuestInvoiceDraft({
      version: GUEST_INVOICE_DRAFT_VERSION,
      values,
      draftClient,
      logoSrc: guestLogoSrc,
      paymentInstructionsDraft,
      pendingIntent: guestPendingIntentRef.current,
    })
  }, [guestMode, values, draftClient, guestLogoSrc, paymentInstructionsDraft])

  const requestGuestSignIn = React.useCallback((intent: GuestPendingIntent) => {
    guestPendingIntentRef.current = intent
    saveGuestInvoiceDraft({
      version: GUEST_INVOICE_DRAFT_VERSION,
      values,
      draftClient,
      logoSrc: guestLogoSrc,
      paymentInstructionsDraft,
      pendingIntent: intent,
    })
    onGuestSignInRequest?.(intent)
  }, [values, draftClient, guestLogoSrc, paymentInstructionsDraft, onGuestSignInRequest])

  React.useEffect(() => {
    if (!guestMode || !guestSignInDismissNonce) return
    guestPendingIntentRef.current = undefined
  }, [guestMode, guestSignInDismissNonce])

  const handleValuesChange = (newValues: any) => {
    setValues(newValues)
  }
  // event handlers
  const handleChange = (event: any) => {
    const auxValues: any = { ...values }
    auxValues[event.target.name] = event.target.value
    if(event.target.name === 'issueDate') {
      // add 7 days to due date
      auxValues['dueDate'] = toDateInputValue(
        new Date(new Date(event.target.value).getTime() + 7 * 24 * 60 * 60 * 1000)
      )
    }
    if (event.target.name === 'clientId' && String(event.target.value ?? '').trim() !== '') {
      setPrereqClientError(false)
    }
    setPristine(false)
    handleValuesChange(auxValues)
  }

  type SubmitOptions = { silent?: boolean; redirectTo?: 'edit' | 'send-email'; successMessage?: string }

  const goToSendEmailPage = React.useCallback(async (invoiceId: string, replaceNewInHistory = false) => {
    const invoiceCacheKey = `/api/invoices/${invoiceId}`
    await mutateCache(invoiceCacheKey, () => getInvoiceById(invoiceCacheKey), { revalidate: false })
    await Promise.all([
      mutateCache(
        `/api/invoices/email?invoiceId=${invoiceId}`,
        () => getEmailTemplate(`/api/invoices/email?invoiceId=${invoiceId}`),
        { revalidate: false },
      ),
      mutateCache(
        `/api/invoices/email-reminder?invoiceId=${invoiceId}`,
        () => getEmailTemplate(`/api/invoices/email-reminder?invoiceId=${invoiceId}`),
        { revalidate: false },
      ),
    ])
    if (replaceNewInHistory) {
      navigate(`/invoices/${invoiceId}`, { replace: true })
    }
    navigate(`/invoices/${invoiceId}/send-email`)
  }, [navigate, mutateCache])

  /** Invoice snapshot first; user profile fills gaps (e.g. logo URL). */
  const businessForDisplay = React.useMemo(() => {
    if (guestMode) {
      return values.businessDetails
    }
    const fromValues = values.businessDetails
    const fromUser = user?.businessDetails
    if (fromValues?.name?.trim()) {
      return { ...fromUser, ...fromValues }
    }
    return fromUser ?? fromValues
  }, [guestMode, values.businessDetails, user?.businessDetails])

  const hasInvoiceClient = guestMode
    ? isCompleteDraftClient(draftClient)
    : hasSelectedInvoiceClient(values.clientId, clients, defaultValues.locked)
  const hasInvoiceBusiness = guestMode
    ? isCompleteGuestBusinessDetails(values.businessDetails)
    : !!(businessForDisplay?.name && String(businessForDisplay.name).trim() !== '')

  const runInvoiceFormValidation = React.useCallback((): boolean => {
    if (!defaultValues.locked) {
      if (!hasInvoiceClient) {
        flushSync(() => {
          setPrereqClientError(true)
          setPrereqBusinessError(false)
          setItemsEmptyError(false)
          setItemFieldErrors({})
          setClientFlashNonce(n => n + 1)
        })
        scrollToValidationTarget({ kind: 'client' }, formRef.current)
        return false
      }
      setPrereqClientError(false)

      if (!hasInvoiceBusiness) {
        flushSync(() => {
          setPrereqBusinessError(true)
          setItemsEmptyError(false)
          setItemFieldErrors({})
          setBusinessFlashNonce(n => n + 1)
        })
        scrollToValidationTarget({ kind: 'business' }, formRef.current)
        return false
      }
      setPrereqBusinessError(false)
    }

    const formEl = formRef.current
    if (formEl && !formEl.reportValidity()) {
      scrollToFirstInvalidField(formEl)
      return false
    }

    const errors = validateForm(values)
    if (errors.length > 0) {
      const scrollTarget = getValidationScrollTarget(values)
      if (scrollTarget?.kind === 'items') {
        flushSync(() => {
          setItemsEmptyError(true)
          setItemsFlashNonce(n => n + 1)
          setItemFieldErrors({})
        })
      } else if (scrollTarget?.kind === 'item') {
        const fieldErrors = getInvalidItemFields(values.items)
        flushSync(() => {
          setItemFieldErrors(fieldErrors)
          setItemsEmptyError(false)
        })
      } else {
        flushSync(() => {
          setItemsEmptyError(false)
          setItemFieldErrors({})
        })
      }
      errors.forEach(error => addAlert(error))
      scrollToValidationTarget(scrollTarget, formRef.current)
      return false
    }
    setItemFieldErrors({})
    setItemsEmptyError(false)
    return true
  }, [defaultValues.locked, hasInvoiceClient, hasInvoiceBusiness, values, addAlert])

  const logoForDisplay = React.useMemo(
    () => user?.businessDetails?.logoSrc
      ?? (values.businessDetails as TBusiness & { logoSrc?: string })?.logoSrc,
    [values.businessDetails, user?.businessDetails?.logoSrc],
  )

  const handleSubmit = async(e: any, options?: SubmitOptions | boolean) => {
    const opts: SubmitOptions = typeof options === 'boolean' ? { silent: options } : options ?? {}
    e.preventDefault()
    try {
      if (!runInvoiceFormValidation()) {
        return false
      }

      const hadSavedPI = !!(user?.businessDetails?.paymentInstructions && String(user.businessDetails.paymentInstructions).trim())
      if (!hadSavedPI && paymentInstructionsDraft.trim()) {
        const piResult = await triggerUpdateBusinessDetails({
          body: { 'businessDetails.paymentInstructions': paymentInstructionsDraft },
        })
        if (!piResult) {
          addAlert({ type: 'error', message: 'Could not save payment instructions' })
          return false
        }
        await mutateUser()
      }

      const valuesWithPI = {
        ...values,
        businessDetails: {
          ...values.businessDetails,
          paymentInstructions: paymentInstructionsDraft,
        },
      }

      const wasNewInvoice = !values._id
      const response: any = await triggerSaveInvoice({ body: valuesWithPI })
      await mutateCache('/api/invoices/next-code')

      const insertedId = response?.data?.insertedId
      if (wasNewInvoice && insertedId) {
        setValues((prev) => ({ ...prev, _id: String(insertedId) }))
      }

      if (opts.redirectTo === 'send-email') {
        const invoiceId = values._id ?? insertedId
        if (!invoiceId) {
          return false
        }
        if (wasNewInvoice) {
          clearNewInvoiceDraftStorage()
          await mutateCache('/api/invoices')
        }
        await goToSendEmailPage(String(invoiceId), wasNewInvoice)
      } else if (wasNewInvoice) {
        clearNewInvoiceDraftStorage()
        await mutateCache('/api/invoices')
        if (insertedId) {
          await mutateCache(`/api/invoices/${insertedId}`)
          navigate(`/invoices/${insertedId}`, { replace: true })
        }
      } else {
        await mutateCache(`/api/invoices/${values._id}`)
      }
      await revalidateInvoiceSummaryCaches(mutateCache)
      if (values.clientId) {
        await revalidateClientCaches(mutateCache)
      }
      if (opts.successMessage) {
        addAlert({ type: 'info', message: opts.successMessage })
      } else if (!opts.silent && opts.redirectTo !== 'send-email') {
        addAlert({ type: 'info', message: 'Invoice saved successfully' })
      }
      await onSuccess?.(response, {
        wasNewInvoice,
        values: valuesWithPI,
        redirectTo: opts.redirectTo,
      })
      setPristine(true)
      setPrereqClientError(false)
      setPrereqBusinessError(false)
      setItemFieldErrors({})
      const savedInvoiceId = values._id ?? insertedId
      return savedInvoiceId ? String(savedInvoiceId) : false
    } catch(error) {
      // do nothing. Global swrConfig handles this
      return false
    }
  }

  const handleSubmitRef = React.useRef(handleSubmit)
  handleSubmitRef.current = handleSubmit

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSavingDraft) return
    setIsSavingDraft(true)
    try {
      await handleSubmit(e)
    } finally {
      setIsSavingDraft(false)
    }
  }

  const handleStatusChange = React.useCallback(async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = event.target.value as InvoiceStatus
    if (newStatus === values.status) return

    const auxValues = { ...values, status: newStatus }
    flushSync(() => {
      setValues(auxValues)
      setPristine(false)
    })

    setIsUpdatingStatus(true)
    try {
      await handleSubmitRef.current({ preventDefault: () => {} } as React.FormEvent, {
        successMessage: 'Invoice status updated',
      })
    } finally {
      setIsUpdatingStatus(false)
    }
  }, [values])

  const downloadPdf = React.useCallback(async (e: any) => {
    e.preventDefault()

    if (guestMode) {
      if (!runInvoiceFormValidation()) return
      requestGuestSignIn('viewPdf')
      return
    }

    if (isPreparingPdf) return
    setIsPreparingPdf(true)
    try {
      if (!runInvoiceFormValidation()) {
        return
      }

      let invoiceId = values._id
      if (!invoiceId || !isPristine) {
        const savedId = await handleSubmitRef.current(e, { silent: true })
        if (!savedId) return
        invoiceId = savedId
      }

      window.open(`${config.REACT_APP_API_URL}/api/invoices/pdf/${invoiceId}`, '_blank')
    } finally {
      setIsPreparingPdf(false)
    }
  }, [guestMode, values._id, isPristine, isPreparingPdf, runInvoiceFormValidation, requestGuestSignIn])

  const handleSentToClientToggle = React.useCallback(async () => {
    const invoiceId = values._id
    if (!invoiceId) return
    if ((values.status ?? defaultValues.status) === InvoiceStatus.PAID) return

    const lastActivity = values.lastActivity ?? defaultValues.lastActivity
    const currentlySent = lastActivity === InvoiceActivity.EMAIL_SENT
      || lastActivity === InvoiceActivity.EMAIL_VIEWED
    const markAsSent = !currentlySent

    setIsTogglingSentToClient(true)
    try {
      const saved = await handleSubmitRef.current({ preventDefault: () => {} }, { silent: true })
      if (!saved) return

      const response = markAsSent
        ? await triggerMarkSentManually({ invoiceId })
        : await triggerUnmarkSentManually({ invoiceId })

      const fallbackValues: Partial<TInvoiceForm> = markAsSent
        ? {
          lastActivity: InvoiceActivity.EMAIL_SENT,
          sentManually: true,
          locked: true,
        }
        : {
          lastActivity: InvoiceActivity.CREATED,
          sentManually: false,
          locked: false,
        }
      const updated = response?.data
      const nextValues: Partial<TInvoiceForm> = updated
        ? {
          lastActivity: updated.lastActivity ?? fallbackValues.lastActivity,
          sentManually: !!updated.sentManually,
          locked: !!updated.locked,
          ...(updated.status != null ? { status: updated.status } : {}),
        }
        : fallbackValues

      setValues((prev: TInvoiceForm) => ({ ...prev, ...nextValues }))
      setPristine(true)
      await mutateCache(`/api/invoices/${invoiceId}`)
      await mutateCache('/api/invoices')
      await mutateCache(`/api/invoices/${invoiceId}/history`)
      await onSuccess?.(response, {
        wasNewInvoice: false,
        values: { ...values, ...nextValues },
      })
    } catch (_err) {
      addAlert({
        type: 'error',
        message: markAsSent ? 'Could not mark invoice as sent' : 'Could not clear sent status',
      })
    } finally {
      setIsTogglingSentToClient(false)
    }
  }, [values, defaultValues.lastActivity, defaultValues.status, triggerMarkSentManually, triggerUnmarkSentManually, mutateCache, addAlert, onSuccess])

  const getCurrencyByCode = (currencyCode: string) => {
    if (guestMode) {
      const staticCurrency = getStaticCurrencyByCode(currencyCode)
      if (!staticCurrency) return undefined
      return {
        _id: staticCurrency.code,
        name: staticCurrency.name,
        code: staticCurrency.code,
        symbol: staticCurrency.symbol,
      }
    }
    return currenciesData?.find((currency: any) => currency.code === currencyCode)
  }

  const onAddNewItem = () => {
    const taxPercentage = guestMode
      ? (values.businessDetails?.tax?.percentage ?? '')
      : (user?.businessDetails?.tax?.percentage ?? '')
    setPristine(false)
    setItemsEmptyError(false)
    handleValuesChange({ ...values, ...{ items: [...values.items, { ...EMPTY_ITEM, taxPercentage }] } })
  }

  const onRemoveItem = (index: number) => {
    const newItems = [...values.items]
    newItems.splice(index, 1)
    setPristine(false)
    setItemFieldErrors({})
    setItemsEmptyError(false)
    handleValuesChange({
      ...values,
      total: utils.calculateTotal(newItems, values?.businessDetails?.enableTax || false),
      totalTax: values?.businessDetails?.enableTax ? utils.calculateTax(newItems) : 0,
      items: newItems,
    })
  }

  const onItemChange = (e: any) => {
    const { name, value } = e.target
    const newItems = [...values.items]
    const index = utils.getLastDigitOfString(name)
    let attribute: string = ''
    if(name.startsWith('invoiceItemDescription')) {
      attribute = 'description'
    } else if(name.startsWith('invoiceItemQuantity')) {
      attribute = 'quantity'
    } else if(name.startsWith('invoiceItemRate')) {
      attribute = 'rate'
    } else if(name.startsWith('invoiceItemAmount')) {
      attribute = 'amount'
    } else if(name.startsWith('invoiceItemTaxPercentage')) {
      attribute = 'taxPercentage'
    }
    if(index !== null) {
      if (attribute === 'quantity' && value !== '' && !Number.isNaN(parseFloat(value)) && parseFloat(value) < 0) {
        return
      }
      const item = newItems[index]
      const newITems = [...values.items]
      const updated = { ...item, [attribute]: value }
      if (attribute === 'quantity' || attribute === 'rate') {
        updated.amount = utils.getItemAmount(updated)
      }
      newITems[index] = updated
      setPristine(false)
      if (attribute === 'description' || attribute === 'quantity' || attribute === 'rate') {
        setItemFieldErrors(prev => {
          if (!prev[index]?.[attribute as ItemFieldKey]) return prev
          const next = { ...prev }
          const itemErrors = { ...next[index] }
          delete itemErrors[attribute as ItemFieldKey]
          if (Object.keys(itemErrors).length === 0) {
            delete next[index]
          } else {
            next[index] = itemErrors
          }
          return next
        })
      }
      handleValuesChange({
        ...values,
        total: utils.calculateTotal(newITems, values?.businessDetails?.enableTax || false),
        totalTax: values?.businessDetails?.enableTax ? utils.calculateTax(newITems) : 0,
        items: newITems
      })
    }
  }

  const isSaveDisabled = () => {
    if(isPristine) {
      return true
    }
    if(defaultValues.locked && values.status === defaultValues.status) {
      return true
    }
    return false
  }

  const cloneAndCreateNew = React.useCallback(() => {
    navigate(`/invoices/new?cloneId=${values._id}`)
  }, [navigate, values._id])

  const handleDuplicateClick = React.useCallback(() => {
    if (isPristine) {
      cloneAndCreateNew()
      return
    }
    setShowDuplicateConfirmModal(true)
  }, [isPristine, cloneAndCreateNew])

  const handleSaveAndClone = React.useCallback(async () => {
    const invoiceId = values._id
    if (!invoiceId) return
    const saved = await handleSubmitRef.current({ preventDefault: () => {} }, { silent: true })
    if (!saved) return
    setShowDuplicateConfirmModal(false)
    navigate(`/invoices/new?cloneId=${invoiceId}`)
  }, [navigate, values._id])

  const defaultValuesRef = React.useRef(defaultValues)
  defaultValuesRef.current = defaultValues
  const previousInvoiceIdRef = React.useRef(defaultValues._id)

  React.useEffect(() => {
    const nextId = defaultValues._id
    if (!nextId || nextId === previousInvoiceIdRef.current) return
    previousInvoiceIdRef.current = nextId
    const resetValues = defaultValuesRef.current
    setValues(resetValues)
    setPristine(true)
    setPaymentInstructionsDraft(
      resolvePaymentInstructions(
        false,
        resetValues?.businessDetails?.paymentInstructions,
        user?.businessDetails?.paymentInstructions,
      )
    )
    setPrereqClientError(false)
    setPrereqBusinessError(false)
    setItemsEmptyError(false)
    setItemFieldErrors({})
    setClientSelectorNonce((n) => n + 1)
  }, [defaultValues._id, user?.businessDetails?.paymentInstructions])

  const clearNewInvoiceDraft = React.useCallback(() => {
    if (!isNewInvoice) return
    clearNewInvoiceDraftStorage()
    const resetValues = defaultValuesRef.current
    setValues(resetValues)
    setPaymentInstructionsDraft(
      user?.businessDetails?.paymentInstructions ?? ''
    )
    setPristine(false)
    setPrereqClientError(false)
    setPrereqBusinessError(false)
    setItemsEmptyError(false)
  }, [isNewInvoice, user?.businessDetails?.paymentInstructions])

  const refreshClientsAfterInlineEdit = async (clientId?: string) => {
    if (clientId) {
      setValues((prev: any) => ({ ...prev, clientId: String(clientId) }))
    }
    setPristine(false)
  }

  const handleInlineClientSaved = async (clientId?: string, savedClient?: SavedClientSnapshot) => {
    if (!clientId) {
      addAlert({
        type: 'error',
        message: 'Client was saved but could not be selected. Please choose it from the list.',
      })
      setShowCreateClientModal(false)
      return
    }
    const id = String(clientId)

    if (savedClient) {
      const snapshot: Client = {
        _id: id,
        tradingName: savedClient.tradingName,
        email: savedClient.email ?? '',
        billingAddress: savedClient.billingAddress ?? '',
      }
      await mutateCache(
        '/api/clients',
        (current: Client[] | undefined) => {
          const list = current ?? []
          if (list.some((c) => String(c._id) === id)) return list
          return [...list, snapshot]
        },
        { revalidate: false },
      )
    }

    flushSync(() => {
      setPrereqClientError(false)
      setValues((prev: any) => {
        const next = { ...prev, clientId: id }
        if (!prev._id) {
          persistNewInvoiceDraft(next, paymentInstructionsDraft)
        }
        return next
      })
      setPristine(false)
      setClientSelectorNonce((n) => n + 1)
    })
    setHandoffDraftClient(undefined)
    setShowCreateClientModal(false)
    await revalidateClientCaches(mutateCache)
  }

  const selectedClientForInvoice = React.useMemo((): Client | undefined => {
    if (!clients || !values.clientId) return undefined
    const id = String(values.clientId)
    return clients.find((c: Client) => String(c._id) === id)
  }, [clients, values.clientId])

  const clientHasEmailForSending = Boolean(selectedClientForInvoice?.email?.trim())

  const needsSaveBeforeSend = !values._id || !isPristine

  const continueToSendEmailPage = React.useCallback(async (e: { preventDefault: () => void }) => {
    if (needsSaveBeforeSend) {
      setIsSendingToEmail(true)
      try {
        return await handleSubmitRef.current(e, { redirectTo: 'send-email' })
      } finally {
        setIsSendingToEmail(false)
      }
    }
    if (values._id) {
      setIsSendingToEmail(true)
      try {
        await goToSendEmailPage(String(values._id))
        return true
      } finally {
        setIsSendingToEmail(false)
      }
    }
    return false
  }, [needsSaveBeforeSend, values._id, goToSendEmailPage])

  const attemptSendInvoice = React.useCallback(async (e: { preventDefault: () => void }) => {
    const enableTax = !!values?.businessDetails?.enableTax
    const zeroTotal = utils.isInvoiceTotalZero(values.items, enableTax)
    if (values.status === InvoiceStatus.OVERDUE && zeroTotal) {
      return
    }
    if (zeroTotal) {
      setShowZeroTotalSendModal(true)
      return
    }
    await continueToSendEmailPage(e)
  }, [values.items, values?.businessDetails?.enableTax, values.status, continueToSendEmailPage])

  const handleZeroTotalSendAnyway = React.useCallback(async () => {
    setShowZeroTotalSendModal(false)
    await continueToSendEmailPage({ preventDefault: () => {} })
  }, [continueToSendEmailPage])

  const handleSendInvoiceClick = React.useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    if (guestMode) {
      if (!runInvoiceFormValidation()) return
      requestGuestSignIn('send')
      return
    }
    const hasClient = hasInvoiceClient
    if (!hasClient) {
      flushSync(() => {
        setPrereqClientError(true)
        setItemsEmptyError(false)
        setItemFieldErrors({})
        setClientFlashNonce((n) => n + 1)
      })
      scrollToValidationTarget({ kind: 'client' }, formRef.current)
      return
    }
    if (!clientHasEmailForSending) {
      setShowEditClientModal(true)
      return
    }
    await attemptSendInvoice(e)
  }, [
    guestMode,
    hasInvoiceClient,
    clientHasEmailForSending,
    attemptSendInvoice,
    runInvoiceFormValidation,
    requestGuestSignIn,
  ])

  const handleInlineClientEditSaved = async (clientId?: string) => {
    await refreshClientsAfterInlineEdit(clientId)
    setShowEditClientModal(false)
  }

  const handleInlineClientEditSavedAndSend = async (clientId?: string, clientEmail?: string) => {
    await refreshClientsAfterInlineEdit(clientId)
    if (!clientEmail?.trim()) {
      addAlert({ type: 'error', message: 'Please add a client email to send the invoice.' })
      return
    }
    setShowEditClientModal(false)
    await attemptSendInvoice({ preventDefault: () => {} })
  }

  const getInvoiceValuesWithBusinessDraft = (prev: any, businessDetailsDraft: BusinessDetailsDraft) => {
    const enableTax = !!businessDetailsDraft['businessDetails.enableTax']
    const defaultTaxPct = businessDetailsDraft['businessDetails.tax.percentage']
    let items = prev.items
    if (enableTax && defaultTaxPct !== '' && defaultTaxPct != null) {
      items = prev.items.map((item: any) => ({
        ...item,
        taxPercentage:
          Number(item.taxPercentage) === 0
            ? item.taxPercentage
            : defaultTaxPct,
      }))
    }
    return {
      ...prev,
      items,
      businessDetails: {
        ...prev.businessDetails,
        name: businessDetailsDraft['businessDetails.name'],
        email: businessDetailsDraft['businessDetails.email'],
        billingAddress: businessDetailsDraft['businessDetails.billingAddress'],
        taxRegistrationNumber: businessDetailsDraft['businessDetails.taxRegistrationNumber'],
        currency: {
          ...prev.businessDetails?.currency,
          code: businessDetailsDraft['businessDetails.currency.code'],
        },
        enableTax: businessDetailsDraft['businessDetails.enableTax'],
        tax: {
          ...prev.businessDetails?.tax,
          name: businessDetailsDraft['businessDetails.tax.name'],
          percentage: businessDetailsDraft['businessDetails.tax.percentage'],
        },
        ...((prev.businessDetails as TBusiness & { logoSrc?: string })?.logoSrc
          ? { logoSrc: (prev.businessDetails as TBusiness & { logoSrc?: string }).logoSrc }
          : {}),
      },
      total: utils.calculateTotal(items, enableTax),
      totalTax: enableTax ? utils.calculateTax(items) : 0,
    }
  }

  const hasTaxChanged = React.useCallback((businessDetailsDraft: BusinessDetailsDraft) => {
    const currentEnableTax = !!values?.businessDetails?.enableTax
    const draftEnableTax = !!businessDetailsDraft['businessDetails.enableTax']
    const currentTaxName = String(values?.businessDetails?.tax?.name || '').trim()
    const draftTaxName = String(businessDetailsDraft['businessDetails.tax.name'] || '').trim()
    const currentTaxPercentage = String(values?.businessDetails?.tax?.percentage ?? '').trim()
    const draftTaxPercentage = String(businessDetailsDraft['businessDetails.tax.percentage'] ?? '').trim()

    return (
      currentEnableTax !== draftEnableTax
      || currentTaxName !== draftTaxName
      || currentTaxPercentage !== draftTaxPercentage
    )
  }, [values?.businessDetails?.enableTax, values?.businessDetails?.tax?.name, values?.businessDetails?.tax?.percentage])

  const applyBusinessDetailsToInvoice = React.useCallback(async (businessDetailsDraft: BusinessDetailsDraft) => {
    const nextValues = getInvoiceValuesWithBusinessDraft(values, businessDetailsDraft)
    setValues(nextValues)
    setPristine(false)

    if (values._id) {
      try {
        await triggerSaveInvoice({ body: nextValues })
        await mutateCache(`/api/invoices/${values._id}`)
      } catch (_err) {
        addAlert({ type: 'error', message: 'Business details were updated, but we could not save this invoice yet.' })
      }
    }

    if (hasTaxChanged(businessDetailsDraft)) {
      setPendingTaxDraft(businessDetailsDraft)
      setShowTaxUpdateScopeModal(true)
    }
  }, [
    addAlert,
    hasTaxChanged,
    mutateCache,
    triggerSaveInvoice,
    values,
  ])

  const externalBusinessApplyNonceRef = React.useRef<number | undefined>()
  React.useEffect(() => {
    if (!externalBusinessDetailsApply) return
    if (externalBusinessApplyNonceRef.current === externalBusinessDetailsApply.nonce) return
    externalBusinessApplyNonceRef.current = externalBusinessDetailsApply.nonce
    void applyBusinessDetailsToInvoice(externalBusinessDetailsApply.draft)
  }, [applyBusinessDetailsToInvoice, externalBusinessDetailsApply])

  const applyTaxToUnsentInvoices = async () => {
    if (!pendingTaxDraft) return
    try {
      const response = await triggerUpdateUnsentInvoicesTax({
        body: {
          enableTax: !!pendingTaxDraft['businessDetails.enableTax'],
          tax: {
            name: pendingTaxDraft['businessDetails.tax.name'],
            percentage: pendingTaxDraft['businessDetails.tax.percentage'],
          },
        },
      })
      addAlert({
        type: 'info',
        message: `Updated tax settings in ${response?.data?.modifiedCount ?? 0} unsent invoices`,
      })
      await mutateCache('/api/invoices')
    } catch (_err) {
      addAlert({ type: 'error', message: 'Could not update unsent invoices tax settings' })
    } finally {
      setShowTaxUpdateScopeModal(false)
      setPendingTaxDraft(null)
    }
  }

  const skipTaxBulkUpdate = () => {
    setShowTaxUpdateScopeModal(false)
    setPendingTaxDraft(null)
  }

  const saveDisabled = isSaveDisabled()
  const invoiceLastActivity = values.lastActivity ?? defaultValues.lastActivity
  const isSentManually = !!(values.sentManually ?? defaultValues.sentManually)
  const isSentInvoice = !isNewInvoice && (
    invoiceLastActivity === InvoiceActivity.EMAIL_SENT
    || invoiceLastActivity === InvoiceActivity.EMAIL_VIEWED
  )
  const isSentViaApp = isSentInvoice && !isSentManually
  const isSentViaTinyInvoice = !isSentManually && (
    invoiceLastActivity === InvoiceActivity.EMAIL_ON_QUEUE
    || invoiceLastActivity === InvoiceActivity.EMAIL_SENT
    || invoiceLastActivity === InvoiceActivity.EMAIL_VIEWED
  )
  const isSentToClient = isSentInvoice
  const isSendingToClient = invoiceLastActivity === InvoiceActivity.EMAIL_ON_QUEUE
  const isPaidInvoice = (values.status ?? defaultValues.status) === InvoiceStatus.PAID
  const sentToClientToggleDisabled = isNewInvoice
    || isSentViaApp
    || isSendingToClient
    || isTogglingSentToClient
    || isPaidInvoice

  const sentToClientHelperText = isSendingToClient
    ? 'This invoice is being sent by email'
    : invoiceLastActivity === InvoiceActivity.EMAIL_FAILED
      ? 'The invoice email could not be delivered. Check the client email address and send again.'
      : isSentToClient
        ? 'This invoice has been sent to your client'
        : isPaidInvoice
          ? 'Delivery cannot be changed on a paid invoice'
          : 'This invoice has not been sent to your client'

  const sentViaAppHistoryKey =
    isSentViaApp && values._id
      ? `/api/invoices/${values._id}/history`
      : null
  const { data: sentViaAppHistory } = useSWR(sentViaAppHistoryKey, getInvoiceHistory)
  const appEmailSentDate = React.useMemo(() => {
    const entries = sentViaAppHistory?.data
    if (!entries?.length) return null
    const sentEntry = entries.find(
      (item: { type: string, message?: string }) =>
        item.type === InvoiceActivity.EMAIL_SENT
        && item.message !== MANUAL_SENT_HISTORY_MESSAGE,
    )
    return sentEntry?.createdDate ?? null
  }, [sentViaAppHistory])
  const appEmailSentTooltip = appEmailSentDate
    ? `This invoice has been sent via email ${formatDateShort(appEmailSentDate, user?.locale)}`
    : isSentViaApp
      ? 'This invoice has been sent via email'
      : undefined

  const sentToClientTooltipMessage = isSentViaApp && appEmailSentTooltip
    ? appEmailSentTooltip
    : !sentToClientToggleDisabled
      ? 'Use this toggle if you sent the invoice yourself. Invoices sent from TinyInvoice.app switch to Sent automatically.'
      : undefined

  const isInvoiceOverdue = values.status === InvoiceStatus.OVERDUE
  const isZeroTotalInvoice = utils.isInvoiceTotalZero(
    values.items,
    !!values?.businessDetails?.enableTax,
  )
  const sendReminderDisabled = isInvoiceOverdue && isZeroTotalInvoice
  const isInvoiceSent = isInvoiceSentToClient({
    lastActivity: invoiceLastActivity,
    sentManually: isSentManually,
  })
  const saveAndSendClientLabel = isInvoiceOverdue ? 'Save & Send Reminder' : 'Save & Send Invoice'

  const isInvoiceLocked = !!(values.locked ?? defaultValues.locked)

  const bottomBarRef = React.useRef<HTMLDivElement>(null)

  const actionSurfaces = React.useMemo(
    () => resolveInvoiceFormActionSurfaces({
      isNewInvoice,
      locked: isInvoiceLocked,
      isMobile,
      isInvoiceOverdue,
      isZeroTotalInvoice,
      isInvoiceSent,
      isPreparingPdf,
      hasDeleteHandler: !!triggerRemoveInvoice,
      isGuest: guestMode,
    }),
    [
      isNewInvoice,
      isInvoiceLocked,
      isMobile,
      isInvoiceOverdue,
      isZeroTotalInvoice,
      isInvoiceSent,
      isPreparingPdf,
      triggerRemoveInvoice,
      guestMode,
    ],
  )

  const bottomBarActive = actionSurfaces.bottomBar.length > 0
  const mobileSidebarOpen = useMobileSidebarOpen()
  const virtualKeyboardOpen = useVirtualKeyboardOpen(bottomBarActive)
  const bottomBarVisible = bottomBarActive && !mobileSidebarOpen && !virtualKeyboardOpen
  useInvoiceFormBottomBarOffset(bottomBarVisible, bottomBarRef)

  const invoiceForActions = React.useMemo(() => ({
    _id: values._id,
    status: values.status ?? defaultValues.status,
    total: values.total,
    lastActivity: invoiceLastActivity,
    sentManually: isSentManually,
  }), [
    values._id,
    values.status,
    values.total,
    defaultValues.status,
    invoiceLastActivity,
    isSentManually,
  ])

  const sharedFormActionProps = React.useMemo(() => ({
    surfaces: actionSurfaces,
    formId: INVOICE_FORM_ID,
    isNewInvoice,
    invoice: invoiceForActions,
    saveDisabled,
    isSavingDraft,
    isLoading,
    isUpdatingBusinessDetails,
    isSendingToEmail,
    isPreparingPdf,
    sendPolicyDisabled: actionSurfaces.sendDisabled,
    sendReminderDisabled,
    onSend: handleSendInvoiceClick,
    onViewPdf: downloadPdf,
    onClearForm: clearNewInvoiceDraft,
    onDuplicate: handleDuplicateClick,
    onDelete: triggerRemoveInvoice
      ? (e: React.MouseEvent) => triggerRemoveInvoiceRef.current?.(e)
      : undefined,
  }), [
    actionSurfaces,
    isNewInvoice,
    invoiceForActions,
    saveDisabled,
    isSavingDraft,
    isLoading,
    isUpdatingBusinessDetails,
    isSendingToEmail,
    isPreparingPdf,
    sendReminderDisabled,
    handleSendInvoiceClick,
    downloadPdf,
    clearNewInvoiceDraft,
    handleDuplicateClick,
    triggerRemoveInvoice,
  ])

  const pageHeaderActions = React.useMemo(() => {
    if (!actionSurfaces.header) {
      return null
    }
    return (
      <InvoiceFormActions
        {...sharedFormActionProps}
        actions={actionSurfaces.header}
        layout='header'
      />
    )
  }, [actionSurfaces.header, sharedFormActionProps])

  React.useEffect(() => {
    if (guestMode) return
    setPageHeader({ actions: pageHeaderActions })
  }, [guestMode, pageHeaderActions, setPageHeader])

  React.useEffect(() => {
    if (guestMode || isNewInvoice || !values.code) {
      return
    }
    const tradingName = selectedClientForInvoice?.tradingName?.trim()
    const description = tradingName
      ? `${values.code} for "${tradingName}"`
      : values.code
    setPageHeader({ description })
  }, [guestMode, isNewInvoice, values.code, selectedClientForInvoice?.tradingName, setPageHeader])

  const handleGuestBusinessFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target
    setValues((prev: TInvoiceForm) => {
      const businessDetails = { ...prev.businessDetails! }
      if (name === 'businessDetails.name') {
        businessDetails.name = value
      } else if (name === 'businessDetails.billingAddress') {
        businessDetails.billingAddress = value
      } else if (name === 'businessDetails.currency.code') {
        const staticCurrency = getStaticCurrencyByCode(value)
        businessDetails.currency = staticCurrency
          ? {
            _id: staticCurrency.code,
            name: staticCurrency.name,
            code: staticCurrency.code,
            symbol: staticCurrency.symbol,
          }
          : { ...businessDetails.currency, code: value }
      }
      return { ...prev, businessDetails }
    })
    setPristine(false)
    if (name === 'businessDetails.name' && value.trim()) {
      setPrereqBusinessError(false)
    }
  }

  const applyGuestTaxDraft = (taxDraft: GuestTaxDraft) => {
    const current = values.businessDetails!
    const businessDetailsDraft: BusinessDetailsDraft = {
      'businessDetails.name': current.name || '',
      'businessDetails.email': current.email || '',
      'businessDetails.billingAddress': current.billingAddress || '',
      'businessDetails.taxRegistrationNumber': taxDraft['businessDetails.taxRegistrationNumber'],
      'businessDetails.currency.code': current.currency?.code || '',
      'businessDetails.enableTax': taxDraft['businessDetails.enableTax'],
      'businessDetails.tax.name': String(taxDraft['businessDetails.tax.name'] || ''),
      'businessDetails.tax.percentage': taxDraft['businessDetails.tax.percentage'],
    }
    setValues((prev: TInvoiceForm) => getInvoiceValuesWithBusinessDraft(prev, businessDetailsDraft))
    setPristine(false)
  }

  const handleDraftClientChange = (nextDraftClient: DraftClient) => {
    setDraftClient(nextDraftClient)
    setPristine(false)
    if (isCompleteDraftClient(nextDraftClient)) {
      setPrereqClientError(false)
    }
  }

  return (
    <div className='col d-flex align-items-center justify-content-center invoice-form'>
      <div className='w-100'>
        <form
          id={INVOICE_FORM_ID}
          ref={formRef}
          noValidate
          onSubmit={handleSaveDraft}
        >
          {!guestMode && !isNewInvoice && (
            <div className='row mb-3 g-2 invoice-form-meta-row'>
              <div className='col-12 col-lg-5'>
                <StatusSelector
                  name='status'
                  id='status'
                  value={values.status}
                  excludeDraft={isSentViaTinyInvoice}
                  onChange={handleStatusChange}
                  disabled={isNewInvoice}
                  loading={isUpdatingStatus}
                />
              </div>
              <div className='col-12 col-lg-7'>
                <SentToClientToggle
                  isSent={isSentToClient}
                  isSending={isSendingToClient}
                  disabled={sentToClientToggleDisabled}
                  loading={isTogglingSentToClient}
                  lastActivity={invoiceLastActivity}
                  sentManually={isSentManually}
                  helperText={sentToClientHelperText}
                  tooltipMessage={sentToClientTooltipMessage}
                  onToggle={handleSentToClientToggle}
                />
              </div>
            </div>
          )}
          <div className={`card border-0 p-4 bg-white invoice-sheet ${invoiceSheetStatusClass(values.status)}`}>
            <div className='row py-2'>
              <div className='col-12 col-lg-4'>
                <FieldLabel label='Bill to' required />
                {guestMode ? (
                  <GuestDraftClientFields
                    draftClient={draftClient}
                    onChange={handleDraftClientChange}
                    showErrors={prereqClientError}
                    flashNonce={clientFlashNonce}
                  />
                ) : (
                  <div
                    key={clientFlashNonce}
                    data-invoice-field='client'
                    className={`${!values?.clientId ? 'pt-4' : ''} invoice-prereq-box position-relative${!defaultValues.locked && clients?.length === 0 ? ' invoice-prereq-box--centered' : ''}${prereqClientError ? ' invoice-prereq-box--error invoice-prereq-box--flash' : ''}`}
                  >
                    <ClientSelector
                      key={`invoice-client-${clientSelectorNonce}-${values?.clientId ?? ''}`}
                      placeHolder='Select or create a client'
                      label=''
                      value={values?.clientId}
                      onChange={handleChange}
                      required={defaultValues.locked}
                      disabled={defaultValues.locked}
                      showNewClient={!defaultValues.locked}
                      onNewClient={() => setShowCreateClientModal(true)}
                      showClientInfo
                    />
                    {prereqClientError && (
                      <small className='text-danger position-absolute start-50 translate-middle-x bottom-0 pb-2'>
                        Required
                      </small>
                    )}
                  </div>
                )}
              </div>
              <div className={`mt-4 mt-lg-0 col-12 col-lg-5 ms-lg-auto${guestMode ? '' : ' text-lg-end'}`}>
                <FieldLabel label='From' className={guestMode ? undefined : 'd-lg-none'} required={guestMode} />
                {guestMode ? (
                  values.businessDetails ? (
                    <GuestInlineBusinessFields
                      businessDetails={values.businessDetails}
                      onChange={handleGuestBusinessFieldChange}
                      showErrors={prereqBusinessError}
                      flashNonce={businessFlashNonce}
                    />
                  ) : null
                ) : businessForDisplay?.name ? (
                  <div data-invoice-field='business'>
                    <div>
                      <p className='fs-2 m-0 p-0 fw-light lh-1'>
                        {businessForDisplay.name}
                      </p>
                    </div>
                    <div className='fs-6 fw-light'>
                      <small>{businessForDisplay.email}</small>
                    </div>
                    <div className='fs-6 fw-light'>
                      <small>{businessForDisplay.billingAddress}</small>
                    </div>
                    <div className='fs-6 fw-light'>
                      <small>
                        {businessForDisplay.taxRegistrationNumber
                          ? <>Tax # {businessForDisplay.taxRegistrationNumber}</>
                          : ''}
                      </small>
                    </div>
                    {!defaultValues.locked && (
                      <div className='mt-1'>
                        <Button icon={<EditIcon width={14} />} iconPosition='start' variant={ButtonVariant.LIGHT} size={ButtonSize.SMALL} onClick={() => openBusinessModal()}>
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <FieldLabel label='Your Business Details' className='d-none d-lg-block' required />
                    <div
                      key={businessFlashNonce}
                      data-invoice-field='business'
                      className={`invoice-prereq-box invoice-prereq-box--centered position-relative ${prereqBusinessError ? 'invoice-prereq-box--error invoice-prereq-box--flash' : ''}`}
                    >
                      <Button type='button' icon={<PlusIcon width={14} height={14} />} iconPosition='start' variant={ButtonVariant.LIGHT} size={ButtonSize.SMALL} onClick={() => openBusinessModal()}>
                        Your details
                      </Button>
                      {prereqBusinessError && <small className='text-danger position-absolute start-50 translate-middle-x bottom-0 pb-2'>Required</small>}
                    </div>
                  </>
                )}
              </div>
              {!guestMode && (logoForDisplay || !defaultValues.locked) && (
                <div className='mt-4 mt-lg-0 col-4 col-lg-3 d-flex flex-column justify-content-center invoice-form-logo-col'>
                  <BusinessLogoEditor
                    imageSrc={logoForDisplay}
                    compact
                    editable={!defaultValues.locked}
                  />
                </div>
              )}
            </div>
            <div className='row mt-4 invoice-meta-fields'>
              <div className='invoice-meta-fields__ref col-lg-3 mt-2 mt-lg-0'>
                <Input
                  label='Invoice Ref #'
                  tooltip='A unique value to identify this invoice.'
                  type='text'
                  name='code'
                  id='code'
                  onChange={handleChange}
                  defaultValue={defaultValues.code}
                  value={values.code}
                  required
                  disabled={defaultValues.locked}
                />
              </div>
              <div className='invoice-meta-fields__issue col-lg-3 mt-2 mt-lg-0'>
                <Input
                  label='Issue Date'
                  type='date'
                  name='issueDate'
                  id='issueDate'
                  onChange={handleChange}
                  defaultValue={toDateInputValue(defaultValues.issueDate)}
                  value={toDateInputValue(values.issueDate)}
                  required
                  disabled={defaultValues.locked}
                />
              </div>
              <div className='invoice-meta-fields__due col-lg-3 mt-2 mt-lg-0'>
                <Input
                  label='Due Date'
                  type='date'
                  name='dueDate'
                  id='dueDate'
                  onChange={handleChange}
                  defaultValue={toDateInputValue(defaultValues.dueDate)}
                  value={toDateInputValue(values.dueDate)}
                  required
                  disabled={defaultValues.locked}
                />
              </div>
            </div>

            <div className='py-3' />

            <div className='row py-3'>
              <InvoiceItems
                addNewItem={onAddNewItem}
                removeItem={onRemoveItem}
                onItemChange={onItemChange}
                itemFieldErrors={hasInvoiceClient && hasInvoiceBusiness ? itemFieldErrors : {}}
                itemsEmptyError={itemsEmptyError && hasInvoiceClient && hasInvoiceBusiness}
                itemsFlashNonce={itemsFlashNonce}

                values={{ items: values.items, total: values?.total, totalTax: values?.totalTax }}
                currency={values?.businessDetails?.currency?.code && getCurrencyByCode(values?.businessDetails?.currency?.code)}
                taxLabel={values?.businessDetails?.enableTax ? values?.businessDetails?.tax?.name : 'Tax Name Not Found'}
                enableTax={values?.businessDetails?.enableTax}
                disabled={defaultValues.locked}
              />
              <div className='row gx-0 gx-lg-3 mt-4'>
                <div className='col'>
                  <TextArea
                    rows={4}
                    label='Payment Instructions'
                    placeholder='Please deposit full amount to bank account: xxx-xxx-xxx-xxxx'
                    disabled={defaultValues.locked}
                    value={paymentInstructionsDraft}
                    optional
                    onChange={(e: any) => {
                      setPaymentInstructionsDraft(e.target.value)
                      setPristine(false)
                    }}
                  />
                </div>
                <div className='col-lg my-4 sm:my-0'>
                  {(!guestMode && values?.businessDetails?.enableTax && !values?.businessDetails?.tax?.name) ? null : (
                    <InvoiceSummary
                      total={values.total}
                      isTaxed={values?.businessDetails?.enableTax || false}
                      taxLabel={values?.businessDetails?.tax?.name}
                      totalTax={values.totalTax}
                      locale={user?.locale}
                      currencyCode={values?.businessDetails?.currency?.code}
                      onEditTax={defaultValues.locked || guestMode ? undefined : () => openBusinessModal('taxName')}
                      guestTaxControl={guestMode ? {
                        configured: isGuestTaxConfigured(values.businessDetails),
                        onClick: () => setShowGuestTaxModal(true),
                      } : undefined}
                    />
                  )}
                </div>
              </div>
            </div>

            {guestMode && (
              <GuestInvoiceActions
                sendLabel={actionSurfaces.sendLabel}
                viewPdfLabel={actionSurfaces.viewPdfLabel}
                isSendingToEmail={isSendingToEmail}
                isPreparingPdf={isPreparingPdf}
                sendDisabled={isSendingToEmail || isLoading || isUpdatingBusinessDetails}
                viewPdfDisabled={isPreparingPdf || isLoading || isUpdatingBusinessDetails}
                onSend={handleSendInvoiceClick}
                onViewPdf={downloadPdf}
              />
            )}
          </div>
          
          {actionSurfaces.footer.length > 0 && (
            <InvoiceFormActions
              {...sharedFormActionProps}
              actions={actionSurfaces.footer}
              layout='footer'
            />
          )}
        </form>
      </div>
      <ClientModal
        open={showCreateClientModal}
        mode='create'
        createDefaultValues={resolveHandoffCreateClientDefaults(
          handoffDraftClient,
          clients?.length ?? 0,
        )}
        onClose={() => setShowCreateClientModal(false)}
        onSave={handleInlineClientSaved}
      />
      <ClientModal
        open={showEditClientModal}
        mode='edit'
        client={selectedClientForInvoice}
        saveAndSendLabel={saveAndSendClientLabel}
        saveAndSendDisabled={sendReminderDisabled}
        onClose={() => setShowEditClientModal(false)}
        onSave={handleInlineClientEditSaved}
        onSaveAndSend={handleInlineClientEditSavedAndSend}
      />
      {!guestMode && (
        <BusinessModal
          open={showEditBusinessModal}
          onClose={closeBusinessModal}
          onSaved={applyBusinessDetailsToInvoice}
          invoiceCurrencyCode={values?.businessDetails?.currency?.code}
          initialFocus={businessModalInitialFocus}
        />
      )}
      {guestMode && values.businessDetails && (
        <GuestTaxModal
          open={showGuestTaxModal}
          onClose={() => setShowGuestTaxModal(false)}
          onSaved={applyGuestTaxDraft}
          initialDraft={buildGuestTaxDraft(values.businessDetails)}
        />
      )}
      <ZeroTotalSendModal
        open={showZeroTotalSendModal}
        onClose={() => setShowZeroTotalSendModal(false)}
        onConfirm={handleZeroTotalSendAnyway}
        isSending={isSendingToEmail}
      />
      <InvoiceOverlayModal
        open={showDuplicateConfirmModal}
        onClose={() => setShowDuplicateConfirmModal(false)}
        title='Unsaved changes'
      >
        <p className='mb-3 text-body-secondary'>
          You have unsaved changes on this invoice.
          <br />
          Save before duplicating so the original is up to date.
        </p>
        <div className='d-flex justify-content-between pt-2'>
          <Button
            type='button'
            size={ButtonSize.LARGE}
            variant={ButtonVariant.LIGHT}
            onClick={() => setShowDuplicateConfirmModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type='button'
            size={ButtonSize.LARGE}
            loading={isLoading}
            loadingText='Saving...'
            onClick={handleSaveAndClone}
            disabled={isLoading}
          >
            Save and clone
          </Button>
        </div>
      </InvoiceOverlayModal>
      <TaxUpdateScopeModal
        open={showTaxUpdateScopeModal}
        onClose={skipTaxBulkUpdate}
        description='Tax has been applied to this invoice and to future invoices. Do you also want to update all existing invoices that have not been sent yet?'
        onConfirm={applyTaxToUnsentInvoices}
        isUpdating={isUpdatingUnsentInvoicesTax}
      />
      {bottomBarVisible && (
        <div ref={bottomBarRef} className='invoice-form-bottom-bar'>
          <InvoiceFormActions
            {...sharedFormActionProps}
            actions={actionSurfaces.bottomBar}
            layout='bottomBar'
          />
        </div>
      )}
    </div>
  )
}

type SummaryProps = {
  total: number | string | undefined,
  taxLabel?: string | undefined,
  totalTax: number | string | undefined,
  isTaxed: boolean,
  locale?: string
  currencyCode?: string
  onEditTax?: () => void
  guestTaxControl?: { configured: boolean; onClick: () => void }
}

const InvoiceSummary: React.FC<SummaryProps> = ({
  isTaxed,
  total,
  taxLabel,
  totalTax,
  locale,
  currencyCode,
  onEditTax,
  guestTaxControl,
}) => {
  const formatSummaryValue = (value: string | number | undefined): string => {
    const numericValue = utils.roundMoney(Number(value ?? 0))
    return new Intl.NumberFormat(locale || undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue)
  }

  const renderSummaryItem = ({ label, name, value, link }: { link: React.ReactNode | undefined, label: string, name: string, value: string | number | undefined }) => {
    return (
      <div className={`py-1 d-flex align-items-center invoice-summary-row ${name === 'total' ? 'invoice-summary-row--total' : ''}`}>
        <div className='col text-start d-flex flex-column flex-wrap gap-2'>
          <b className='mb-0 pt-2'>{label}</b>
          <div>{link}</div>
        </div>
        <div className='col align-self-start'>
          <CalculatedInput
            type='text'
            name={name}
            value={formatSummaryValue(value)}
            className='text-end'
            disabled
          />
        </div>
      </div>
    )
  }
  const showTaxRow = isTaxed && taxLabel
  const guestTaxLink = guestTaxControl
    ? <GuestTaxLink configured={guestTaxControl.configured} onClick={guestTaxControl.onClick} />
    : undefined

  return (
    <div className='col text-end bg-light p-2 invoice-summary-container'>
      {renderSummaryItem({
        label: 'Subtotal',
        name: 'Subtotal',
        value: isTaxed && taxLabel
          ? utils.roundMoney(Number(total ?? 0) - Number(totalTax ?? 0))
          : Number(total ?? 0),
        link: guestTaxControl && !showTaxRow ? guestTaxLink : undefined,
      })}
      {showTaxRow && renderSummaryItem({
        label: `${taxLabel} ` || '',
        name: 'totalTax',
        value: totalTax,
        link: guestTaxControl
          ? guestTaxLink
          : onEditTax ? (
            <Button
              type='button'
              icon={<EditIcon width={14} />}
              iconPosition='start'
              variant={ButtonVariant.LIGHT}
              size={ButtonSize.SMALL}
              onClick={onEditTax}
            >
              Edit
            </Button>
          ) : undefined,
      })}
      {renderSummaryItem({
        label: `Total (${currencyCode ?? ''})`,
        name: 'total',
        value: total,
        link: undefined,
      })}
    </div>
  )
}

export default InvoiceForm
